import { useCallback, useEffect, useState } from "react";
import { Phone, Plus } from "lucide-react";
import Select2 from "../Select2";
import { todoOutsidePartyApi } from "../../api/todoOutsidePartyApi";
import OutsidePersonModal from "./OutsidePersonModal";
import { unwrap } from "./todoShared";

/** Picker text for one person: "Name, Place · mobile". */
export const personOptionLabel = (p) => `${p.label || p.name}${p.mobile ? ` · ${p.mobile}` : ""}`;

/**
 * WHO DOES IT OUTSIDE THE FACTORY — a person from the outside people list,
 * with "+ New" to add one on the spot (picked at once) and a link to the
 * list. Calls onChange(person | null).
 *
 * `current` is what an edited task already has ({ id, label }): kept as an
 * option even after that person was removed from the list, so the task can
 * still be saved. `legacyText` is what an older task had typed instead
 * (no id) — shown until someone is picked.
 */
export default function OutsidePartyPicker({ value, onChange, current, legacyText, disabled }) {
  const [people, setPeople] = useState([]);
  const [adding, setAdding] = useState(false);

  const load = useCallback(() => todoOutsidePartyApi.list()
    .then((res) => { const list = unwrap(res) || []; setPeople(list); return list; })
    .catch(() => { setPeople([]); return []; }), []);

  useEffect(() => { load(); }, [load]);

  const options = people.map((p) => ({ value: String(p.id), label: personOptionLabel(p) }));
  if (current?.id && !people.some((p) => String(p.id) === String(current.id))) {
    options.unshift({ value: String(current.id), label: `${current.label} (removed from the list)` });
  }
  const picked = people.find((p) => String(p.id) === String(value));

  const pick = (v) => {
    if (!v) { onChange(null); return; }
    const p = people.find((x) => String(x.id) === String(v));
    onChange(p || (current && String(current.id) === String(v) ? { id: current.id, label: current.label } : null));
  };

  return (
    <div>
      <div style={{ display: "flex", gap: 6, alignItems: "flex-start" }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <Select2 value={value || ""} onChange={pick} options={options} disabled={disabled}
            placeholder={people.length ? "Choose who / where…" : "No outside people yet — add one"} />
        </div>
        <button type="button" onClick={() => setAdding(true)} disabled={disabled} title="Add a new outside person" style={{
          display: "inline-flex", alignItems: "center", gap: 4, padding: "8px 10px", borderRadius: 8, whiteSpace: "nowrap",
          border: "1px solid var(--border)", background: "var(--bg-input)", color: "var(--primary)", cursor: "pointer",
          fontSize: 13, fontWeight: 700, fontFamily: "inherit",
        }}><Plus size={13} /> New</button>
      </div>
      {picked && (picked.mobile || picked.notes) && (
        <div style={{ fontSize: 12, color: "var(--text-2)", marginTop: 5, display: "flex", gap: 10, flexWrap: "wrap" }}>
          {picked.mobile && <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}><Phone size={11} />{picked.mobile}</span>}
          {picked.notes && <span>{picked.notes}</span>}
        </div>
      )}
      {!value && legacyText && (
        <div style={{ fontSize: 12, color: "var(--warn)", marginTop: 5 }}>
          Typed earlier: “{legacyText}” — pick from the list to link it, or leave it as it is.
        </div>
      )}
      <div style={{ fontSize: 11.5, color: "var(--text-2)", marginTop: 5 }}>
        <a href="#todo-outside-people" target="_blank" rel="noreferrer" style={{ color: "var(--primary)", fontWeight: 600 }}>
          Manage outside people
        </a>
        {" "}— add, change or remove people on the list.
      </div>
      <OutsidePersonModal open={adding} onClose={() => setAdding(false)}
        onSaved={async (p) => {
          setAdding(false);
          await load();
          if (p) onChange(p);
        }} />
    </div>
  );
}
